// Smart Sash Admin Panel - RFID Tag Store
import { PaginationMetadata, RfidTagListItem, RfidTagsQueryParams } from '@/models';
import { rfidService } from '@/services/api/rfidService';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

interface RfidTagState {
  tags: RfidTagListItem[];
  pagination: PaginationMetadata | null;
  isLoading: boolean;
  error: string | null;
  errorStatus: number | null;
  fetchTags: (query: RfidTagsQueryParams) => Promise<void>;
  clearError: () => void;
}

interface MaybeHttpError {
  response?: { status?: number };
}

function extractStatus(error: unknown): number | null {
  const status = (error as MaybeHttpError | null)?.response?.status;
  return typeof status === 'number' ? status : null;
}

export const useRfidTagStore = create<RfidTagState>()(
  devtools(
    (set) => ({
      tags: [],
      pagination: null,
      isLoading: false,
      error: null,
      errorStatus: null,
      fetchTags: async (query: RfidTagsQueryParams) => {
        set({ isLoading: true, error: null, errorStatus: null });
        try {
          const response = await rfidService.getTags(query);
          set({
            tags: response.data,
            pagination: response.pagination,
            isLoading: false,
          });
        } catch (error: unknown) {
          set({
            error: getErrorMessage(error, 'Failed to load RFID tags'),
            errorStatus: extractStatus(error),
            isLoading: false,
            tags: [],
            pagination: null,
          });
        }
      },
      clearError: () => set({ error: null, errorStatus: null }),
    }),
    { name: 'RfidTagStore' }
  )
);
